import {controlGeometry, layoutKey, mobileControlDefault, mobileControlVisible, levelLauncherGeometry} from '../input/mobileLayout.js';
import {levelKeyId, levelButtonBase} from '../input/mobileLevelControls.js';

export class MobileControlView {
    constructor(target, buttonSize = 80) {
        Object.assign(this, createLayout(target, buttonSize));
        this.mode = 'vehicle';
        this.jumpEnabled = false;
        this.levelButtons = {};
        this.levelLayout = {};
        this.transforms = {};
        for (const [id, element] of Object.entries(this.controls)) {
            this.transforms[id] = element.style.transform;
            element.dataset.mobileControl = id;
        }
        // The directional pad's holder has no dimensions in the original layout.
        // Its four arrows are arranged around the center of a single arrow.
        this.controls.dpad.style.transformOrigin = `${buttonSize * .4}px ${buttonSize * .4}px`;
        this.root.style.setProperty('--mobile-safe-left', 'env(safe-area-inset-left, 0px)');
        this.root.style.setProperty('--mobile-safe-right', 'env(safe-area-inset-right, 0px)');
        this.root.style.setProperty('--mobile-safe-top', 'env(safe-area-inset-top, 0px)');
        this.root.style.setProperty('--mobile-safe-bottom', 'env(safe-area-inset-bottom, 0px)');
    }
    resize(width, height, orientation, layout = {}) {
        this.root.style.width = `${width}px`;
        this.root.style.height = `${height}px`;
        for (const id of ['left', 'right', 'brake', 'accelerate', 'flip']) {
            const size = orientation === 'landscape' ? 80 : 50;
            this.controls[id].style.width = `${size}px`;
            this.controls[id].style.height = `${size}px`;
        }
        this.controls.pause.style.width = this.controls.pause.style.height = '50px';
        const style = getComputedStyle(this.root);
        this.viewport = {width, height, insets: Object.fromEntries(['left', 'right', 'top', 'bottom'].map(side =>
            [side, Math.max(8, parseFloat(style.getPropertyValue(`--mobile-safe-${side}`)) || 0)]))};

        // Measure the unchanged responsive layout, including controls currently
        // hidden by the vehicle/character mode, without exposing an extra frame.
        const elements = [this.root, this.vehicle, ...Object.values(this.controls)];
        const display = elements.map(element => element.style.display);
        const hidden = elements.map(element => element.hidden);
        const visibility = this.root.style.visibility;
        this.root.style.visibility = 'hidden';
        elements.forEach(element => element.hidden = false);
        elements.forEach(element => element.style.display = element === this.root || element === this.vehicle || element === this.controls.dpad ? 'block' : '');
        for (const [id, element] of Object.entries(this.controls)) element.style.transform = this.transforms[id];
        const root = this.root.getBoundingClientRect();
        const zoom = root.width / width || 1;
        this.bases = {};
        for (const [id, element] of Object.entries(this.controls)) {
            const boxes = (id === 'dpad' ? Object.values(this.directions) : [element]).map(node => node.getBoundingClientRect());
            const left = Math.min(...boxes.map(box => box.left)), top = Math.min(...boxes.map(box => box.top));
            this.bases[id] = {left: (left - root.left) / zoom, top: (top - root.top) / zoom,
                width: (Math.max(...boxes.map(box => box.right)) - left) / zoom,
                height: (Math.max(...boxes.map(box => box.bottom)) - top) / zoom};
        }
        elements.forEach((element, index) => element.style.display = display[index]);
        elements.forEach((element, index) => element.hidden = hidden[index]);
        this.root.style.visibility = visibility;
        this.apply(layout);
    }
    apply(layout) {
        this.layout = layout;
        this.geometry = {};
        for (const [id, element] of Object.entries(this.controls)) {
            const base = this.bases[id];
            const defaults = mobileControlDefault(id, this.mode, this.bases, this.viewport);
            const geometry = id === 'levelControls'
                ? levelLauncherGeometry(this.geometry.pause, layout[id], this.viewport)
                : controlGeometry(defaults, layout[layoutKey(id, this.mode)], this.viewport);
            this.geometry[id] = geometry;
            const dx = geometry.x - base.left - base.width / 2, dy = geometry.y - base.top - base.height / 2;
            element.style.transform = `translate(${dx}px, ${dy}px) scale(${geometry.scale}) ${this.transforms[id]}`;
        }
        this.applyLevelLayout(this.levelLayout);
    }
    setLevelKeys(keys, label) {
        for (const button of Object.values(this.levelButtons)) button.remove();
        this.levelButtons = {};
        for (const code of keys) {
            const button = document.createElement('button');
            button.type = 'button';
            button.className = 'mobile-level-key';
            button.dataset.mobileControl = levelKeyId(code);
            button.dataset.levelKey = code;
            button.textContent = label(code);
            Object.assign(button.style, {position: 'absolute', margin: '0', padding: '3px', boxSizing: 'border-box',
                width: '52px', height: '52px', border: '3px solid #585858', borderRadius: '14px',
                background: '#383838', color: 'white', font: '700 13px Montserrat, sans-serif',
                overflowWrap: 'anywhere', touchAction: 'none', userSelect: 'none', pointerEvents: 'all'});
            this.root.appendChild(button);
            this.levelButtons[levelKeyId(code)] = button;
        }
    }
    applyLevelLayout(layout = {}) {
        this.levelLayout = layout;
        if (!this.viewport) return;
        for (const [id, button] of Object.entries(this.levelButtons)) {
            button.hidden = !layout[id];
            this.bases[id] = levelButtonBase;
            const box = controlGeometry(levelButtonBase, layout[id], this.viewport);
            this.geometry[id] = box;
            Object.assign(button.style, {left: `${box.left}px`, top: `${box.top}px`,
                transformOrigin: 'top left', transform: `scale(${box.scale})`});
        }
    }
    levelKeyObstacles() {
        const boxes = [];
        for (const mode of ['vehicle', 'character', 'runner']) for (const id of Object.keys(this.controls)) {
            if (id === 'levelControls') continue;
            if (mobileControlVisible(id, mode)) boxes.push(controlGeometry(
                mobileControlDefault(id, mode, this.bases, this.viewport), this.layout[layoutKey(id, mode)], this.viewport));
        }
        boxes.push(this.geometry.levelControls);
        for (const id of Object.keys(this.levelButtons)) if (this.levelLayout[id]) boxes.push(this.geometry[id]);
        return boxes;
    }
    setMode(mode, layout = this.layout) {
        this.mode = mode;
        this.vehicle.style.display = mode === 'vehicle' ? 'block' : 'none';
        for (const id of ['eject', 'grab', 'dpad']) {
            this.controls[id].style.display = mobileControlVisible(id, mode) ? 'block' : 'none';
        }
        this.controls.jump.style.display = this.jumpEnabled && mobileControlVisible('jump', mode) ? 'block' : 'none';
        // Preserve the space these inline buttons occupy so hiding them does
        // not move Flip's measured origin or any saved Flip position.
        for (const id of ['left', 'right', 'flip']) {
            this.controls[id].style.visibility = mobileControlVisible(id, mode) ? '' : 'hidden';
        }
        if (this.bases) this.apply(layout);
    }
    setJumpEnabled(enabled) {
        this.jumpEnabled = enabled;
        this.controls.jump.style.display = enabled && mobileControlVisible('jump', this.mode) ? 'block' : 'none';
    }
}

// The gameplay overlay and layout preview share the original artwork and layout.
const createLayout = (target, buttonSize) => {
	const layoutHolder = document.createElement('div');
	layoutHolder.style = `
		position:absolute;
		width:100%;
		height:100%;
		left:0;
		top:0;

		pointer-events:none;
		opacity:${1}
	`;
	target.appendChild(layoutHolder);




	const screenMargin = '20px 40px';
	const buttonMargin = '0px';
	const verticalAngularOffset = '60px';

	const rotateButtons = document.createElement('div');
	const arrowLeft = createButton('arrow', buttonSize, rotateButtons, buttonMargin);
	arrowLeft.style.marginBottom = verticalAngularOffset;
	const arrowRight = createButton('arrow', buttonSize, rotateButtons, buttonMargin, true);
	const flip = createButton('flip', buttonSize, rotateButtons, buttonMargin, true);
	flip.style.marginLeft = '20px';
	rotateButtons.style = `
	position:absolute;
	left:0;
	bottom:0;
	margin:${screenMargin};
	`;
	layoutHolder.appendChild(rotateButtons);

	const accelerateButtons = document.createElement('div');
	const accelDown = createButton('accel', buttonSize, accelerateButtons, buttonMargin, true);
	const accelUp = createButton('accel', buttonSize, accelerateButtons, buttonMargin);
	accelUp.style.marginBottom = verticalAngularOffset;
	accelerateButtons.style = `
	position:absolute;
	right:0;
	bottom:0;
	margin:${screenMargin};
	`;
	const pauseButton = createButton('pause', buttonSize * .5, layoutHolder, buttonMargin);
	pauseButton.style.position = 'absolute';
	pauseButton.style.top = '0';
	pauseButton.style.right = '0';
	pauseButton.style.margin = '20px';

	layoutHolder.appendChild(accelerateButtons);

	const ejectButton = createButton('eject', buttonSize * .5, layoutHolder, buttonMargin);
	ejectButton.style.position = 'absolute';
	ejectButton.style.bottom = '120px';
	ejectButton.style.left = '0';
	ejectButton.style.margin = '60px';

	const grabButton = createButton('grab', buttonSize * .5, layoutHolder, buttonMargin);
	grabButton.style.position = 'absolute';
	grabButton.style.bottom = '134px';
	grabButton.style.right = `${buttonSize * .5 + 34}px`;
	grabButton.style.margin = '60px';
	grabButton.style.display = 'none';

	const jumpButton = createButton('jump', buttonSize * .7, layoutHolder, buttonMargin);
	jumpButton.style.position = 'absolute';
	jumpButton.style.bottom = '120px';
	jumpButton.style.right = '135px';
	jumpButton.style.display = 'none';

	const actionButton = createButton('action', buttonSize * .5, layoutHolder, buttonMargin);
	actionButton.style.position = 'absolute';
	actionButton.style.bottom = '134px';
	actionButton.style.right = '0';
	actionButton.style.margin = '60px 46px';

	const dpadHolder = document.createElement('div');

	const dpadSpread = 40;
	const dpadScale = .8;
	const dpadUp = createButton('dpad', buttonSize * dpadScale, dpadHolder, buttonMargin, false, `translate(0, -${dpadSpread}px)`);
	const dpadDown = createButton('dpad', buttonSize * dpadScale, dpadHolder, buttonMargin, false, `rotate(180deg) translate(0, -${dpadSpread}px)`);
	const dpadLeft = createButton('dpad', buttonSize * dpadScale, dpadHolder, buttonMargin, false, `rotate(270deg) translate(0, -${dpadSpread}px)`);
	const dpadRight = createButton('dpad', buttonSize * dpadScale, dpadHolder, buttonMargin, false, `rotate(90deg) translate(0, -${dpadSpread}px)`);
	const dpadTouchArea = document.createElement('div');
	dpadTouchArea.className = 'mobile-dpad-touch-area';
	Object.assign(dpadTouchArea.style, {position: 'absolute', left: `${-dpadSpread}px`, top: `${-dpadSpread}px`,
		width: `${buttonSize * dpadScale + dpadSpread * 2}px`, height: `${buttonSize * dpadScale + dpadSpread * 2}px`,
		pointerEvents: 'none'});
	dpadHolder.appendChild(dpadTouchArea);

	dpadHolder.style = `
	position:absolute;
	right:150px;
	bottom:120px;
	`;
	layoutHolder.appendChild(dpadHolder);

	const levelControls = document.createElement('button');
	levelControls.type = 'button';
	levelControls.className = 'mobile-level-toggle';
	Object.assign(levelControls.style, {position: 'absolute', left: '0px', top: '0px', width: '168px', height: '44px',
		boxSizing: 'border-box', margin: '0', padding: '4px', border: '2px solid #666', borderRadius: '10px',
		background: '#383838', color: 'white', font: '700 12px/14px Montserrat, sans-serif',
		whiteSpace: 'normal', overflowWrap: 'anywhere', pointerEvents: 'all', touchAction: 'manipulation'});
	layoutHolder.appendChild(levelControls);

	return {root: layoutHolder, controls: {left: arrowLeft, right: arrowRight, flip, brake: accelDown, accelerate: accelUp, pause: pauseButton, eject: ejectButton, jump: jumpButton, grab: grabButton, action: actionButton, dpad: dpadHolder, levelControls}, directions: {up: dpadUp, down: dpadDown, left: dpadLeft, right: dpadRight}, vehicle: accelerateButtons, dpadTouchArea};
};

const createButton = (type, size, target, margin, mirrorX, transform) => {
	const box = 100;
	const svgElement = document.createElementNS("http://www.w3.org/2000/svg", 'svg');
	svgElement.setAttributeNS(null, "viewBox", `0 0 ${box} ${box}`);
	svgElement.setAttributeNS(null, "width", `${size}`);
	svgElement.setAttributeNS(null, "height", `${size}`);

	let generatedHTML =
		`
	<style>.mc5 {display: inline;fill-rule: evenodd;clip-rule: evenodd;fill: #fff}	</style><linearGradient id="edge_1_" gradientUnits="userSpaceOnUse" x1="7599.08" y1="7903.88" x2="9647.08" y2="7903.88"	gradientTransform="matrix(0 -.04883 -.04883 0 435.932 471.049)"><stop offset="0" stop-color="#383838" /><stop offset="1" stop-color="#585858" /></linearGradient><path id="edge" d="M80.3 80.3C88.7 72 92.9 61.9 92.9 50c0-11.8-4.2-21.9-12.6-30.3C72 11.3 61.9 7.1 50 7.1c-11.9 0-22 4.2-30.3 12.6C11.3 28.1 7.1 38.2 7.1 50c0 11.9 4.2 22 12.6 30.3C28 88.7 38.1 92.9 50 92.9c11.9 0 22-4.2 30.3-12.6m5-65.7C95.1 24.4 100 36.2 100 50s-4.9 25.6-14.7 35.3C75.6 95.1 63.8 100 50 100c-13.9 0-25.7-4.9-35.4-14.7C4.9 75.6 0 63.8 0 50s4.9-25.6 14.6-35.4C24.3 4.9 36.1 0 50 0c13.8 0 25.6 4.9 35.3 14.6" fill="url(#edge_1_)" /><path id="bg"	d="M80.3 80.3C72 88.7 61.9 92.9 50 92.9c-11.9 0-22-4.2-30.3-12.6C11.3 72 7.1 61.9 7.1 50c0-11.8 4.2-21.9 12.6-30.3C28 11.3 38.1 7.1 50 7.1c11.9 0 22 4.2 30.3 12.6 8.4 8.4 12.6 18.5 12.6 30.3 0 11.9-4.2 22-12.6 30.3"	fill="#383838" />
	${type === 'arrow'  ? `
	<path d="M545.7 4335.9c103.3 6.8 195.9 73.7 235.2 170.1 29.2 71.5 26.7 153-6.8 222.6-23.1 48.4-61.3 89.8-107.8 117.5-33.1 19.8-73.2 33-112.2 36.9-11.8 1.1-39.7 1.4-51.2.4-115.9-10.1-214-93.7-242.4-206.5-2.2-8.7-4.7-21.3-5.5-27.7-.7-6.4-1.6-12.9-2-14.4l-.5-2.8-81.2-.2L314.1 4496s22.3 19.3 70 66.7c36.6 36.2 66.3 69.5 66.3 69.5s-79.9.2-80.2.8c-.9 1.4 2.5 15.8 6 26.7 7.4 22.4 20.5 43.8 37.4 60.9 30.8 31.1 68.3 47 112 47.3 20.4 0 31-1.5 49.2-7.4 25.8-8.2 46.6-21.3 65.7-41.2 29.3-30.5 44.2-67.3 44.2-109.4-.1-44.5-16.4-82.6-48.5-113.8-25.5-24.7-56.7-39.5-91.9-43.7" fill="#fff" transform="matrix(.1 0 0 -.1 0 511)" id="arrow" />
	` : ''}
	${type === 'accel' ? `
	<g id="accel"><path class="mc5"	d="M58.7 28.4v13.9H40.9v15.4h17.8v13.9L81.1 50 58.7 28.4M24.9 42.3h-6v15.4h6V42.3zM38.1 42.3H27.5v15.4h10.6V42.3z" /></g>
	` : ''}
	${type === 'flip' ? `
	<path d="M48.8 60.9c-3.1-.1-6.7-.6-10.6-1.4-5.3-1.1-9.7-2.5-13.1-4.1-6.3-3.2-6.5-6.5-5.5-8.7 1.3-3 5.4-5.1 13.5-7 5.7-1.1 11.4-1.7 17-1.7 6.2 0 12.4.7 18.6 2.1 3.3.7 5.9 1.7 8 3 2.5 1.5 3.9 3.3 4.2 5.4 1 6.7-8.8 9.1-12.1 9.9-.8.2-1.5.4-2.1.5l-3.2.7v-8.1l2-.5c.4-.1.7-.2 1-.2 4.3-1 5.4-2.1 5.7-2.4-.3-.3-1.3-1.2-4.7-2.1-5.7-1.2-11.6-1.8-17.3-1.8-5.3 0-10.6.5-15.9 1.5-2.9.9-4.5 1.6-5.3 2.1.6.3 1.5.7 3 1.1 2.8.8 6.4 1.6 10.8 2.1 2.4.3 4.4.5 6 .6v-7l11.4 11.4-11.4 11.4v-6.8z" fill="#fff" id="flip" />
	` : ''}
	${type === 'pause' ? `
	<g id="pause"><path id="r2" class="mc5" d="M32.8 30.5h10.5v39.1H32.8z" /><path id="r" class="mc5" d="M57 30.6h10.5v39.1H57z" /></g>
	` : ''}
	${type === 'exit' ? `
	<g id="exit"><path class="mc5"d="M31.3 74.6c-3.5 0-6.3-2.8-6.3-6.3V56.8h7.3v10.5h34.9V32.4H32.3V43H25V31.4c0-3.5 2.8-6.3 6.3-6.3h36.9c3.5 0 6.3 2.8 6.3 6.3v36.9c0 3.5-2.8 6.3-6.3 6.3H31.3zm12.1-15.2l5.9-5.9H25.1v-7.3h24.2l-5.9-5.9 5.1-5.1 14.6 14.6-14.6 14.7-5.1-5.1z" id="exit-to-app_6_" /></g>
	` : ''}
	${type === 'action' ? `
	<path class="mc5" d="M58.9 63.1l-2-5.4H43.1l-2 5.4h-7.7l12.3-32h8.5l12.3 32h-7.6zM50 37.9l-5 13.8h10l-5-13.8z" id="action"/>
	` : ''}
	${type === 'jump' ? `
	<g id="jump"><path class="mc5" d="M50 22L75 49H60v18H40V49H25z"/><path class="mc5" d="M27 73h46v7H27z"/></g>
	` : ''}
	${type === 'grab' ? `
	<g id="grab"><path fill="#fff" d="M39 78L23 55c-3-4-2-8 1-10s6-1 9 3l3 4V32c0-7 10-7 10 0V24c0-7 10-7 10 0v5c0-7 10-7 10 0v9c0-7 10-7 10 0v20c0 9-5 15-11 20v4H39z"/><path d="M46 33v17m10-20v20m10-10v12" fill="none" stroke="#383838" stroke-width="3" stroke-linecap="round"/></g>
	` : ''}
	${type === 'eject' ? `
	<g id="eject"><circle class="mc5" cx="56.6" cy="20.1" r="5.3"/><path class="mc5" d="M41.6 66.2l15.6 3.7-12.1 16.3-3.5-20zm5.2 13.6l5.8-7.8-7.5-1.8 1.7 9.6zM31 55.8l5.2 1.2 3.2-13.4 13.4 3.2 4.4-18.7-5.3-1.2-3.1 13.4-13.4-3.2z"/><path class="mc5" d="M59.9 39.8l5.2 1.2-5.6 23.9-18.7-4.4L43.3 50l13.4 3.2z"/></g>
	` : ''}
	`;

	if(type === 'dpad'){
		generatedHTML = `<svg version="1.1" id="l" xmlns="http://www.w3.org/2000/svg" x="0" y="0" viewBox="0 0 61 75.5" xml:space="preserve"><style>.mc6{fill:#383838}</style><linearGradient id="SVGID_1_" gradientUnits="userSpaceOnUse" x1="652.814" y1="13141.685" x2="2291.214" y2="13141.685" gradientTransform="matrix(0 -.04608 -.04608 0 636.09 105.583)"><stop offset="0" stop-color="#383838"/><stop offset="1" stop-color="#585858"/></linearGradient><path d="M56 0c3.3 0 5 1.7 5 5v40L30.5 75.5 0 45V5c0-3.3 1.7-5 5-5h51m1 5c0-.7-.3-1-1-1H5c-.7 0-1 .3-1 1v38.3l26.5 26.5L57 43.3V5" fill="url(#SVGID_1_)"/><path class="mc6" d="M57 5v38.3L30.5 69.9 4 43.3V5c0-.7.3-1 1-1h51c.7 0 1 .3 1 1"/><path class="mc6" d="M31 15c-.3 0-.5.1-.7.3l-5 5c-.3.2-.3.5-.3.7v.4c.2.4.5.6.9.6h10c.4 0 .7-.2.9-.6.2-.4.1-.8-.2-1.1l-5-5c-.1-.2-.4-.3-.6-.3"/><path d="M31 15c.2 0 .5.1.7.3l5 5c.3.3.4.7.2 1.1-.2.4-.5.6-.9.6H26c-.4 0-.7-.2-.9-.6V21c0-.2 0-.5.2-.7l5-5c.2-.2.4-.3.7-.3" fill="#666"/></svg>`;
	}

	if (mirrorX) svgElement.style.transform = 'scale(-1, 1)';
	if (margin) svgElement.style.margin = margin;
	if (transform){
		svgElement.style.position = 'absolute';
		svgElement.style.transform = transform;
	}
	svgElement.innerHTML = generatedHTML
	svgElement.style.pointerEvents = 'all';
	svgElement.style.cursor = 'pointer';
	svgElement.style.userSelect = 'none';
	target.appendChild(svgElement);

	return svgElement;
}
